import React, { FunctionComponent } from 'react'
import { View } from 'react-native'

import * as WebBrowser from 'expo-web-browser'
import styled from 'styled-components/native'

import { WORK_LIFE_SERVICES_EVENTS, WorkLifeServicesShape } from '@lyrahealth-inc/shared-app-logic'

import { BodyText, SecondaryButton } from '../../atoms'
import { CheckMarkBullet } from '../../atoms/icons/WorkLifeIcons'
import { IS_WEB } from '../../constants'
import { openUrl, ThemeType } from '../../utils'

export type WorkLifeCardProps = WorkLifeServicesShape & {
  track: (event: string, props?: Record<string, unknown>) => void
  customerName?: string
  workLifeServiceDependentCareCode?: string
}

const Card = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: theme.breakpoints.isMobileSized ? '100%' : 'calc(50% - 8px)',
  backgroundColor: theme.colors.backgroundPrimary,
  borderRadius: '16px',
  padding: theme.breakpoints.isMobileSized ? '24px 16px' : '24px 32px',
  minWidth: '300px',
}))

const IconContainer = styled.View({
  display: 'flex',
  marginBottom: '16px',
})
const CardTitle = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  fontSize: '22px',
  color: theme.colors.textPrimary,
  marginBottom: theme.spacing['16px'],
}))

export const CardDescription = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  fontSize: '18px',
  color: theme.colors.textSecondary,
  marginBottom: theme.spacing['24px'],
}))

const BulletsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
}))

const CardButtonContainer = styled.View<{ theme: ThemeType }>(() => ({
  marginTop: 'auto',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
}))

const BulletItem = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginBottom: '8px',
})

export const BulletText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  fontSize: '16px',
  color: theme.colors.textSecondary,
  flex: 1, // Allows bullet text in cards to wrap properly on Android
}))

const CardCTAButton = styled(SecondaryButton)<{ theme: ThemeType }>(() => ({
  ...(!IS_WEB && { width: '100%' }),
}))

export const WorkLifeCard: FunctionComponent<WorkLifeCardProps> = ({
  iconSvg,
  title,
  paragraph,
  bullets,
  identifiers,
  PATH,
  CTA,
  customerName,
  workLifeServiceDependentCareCode,
  track,
}: WorkLifeCardProps) => {
  const capitalize = (str: string) => {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''
  }
  return (
    <Card>
      <IconContainer>{iconSvg}</IconContainer>
      <CardTitle text={title} />
      <>
        {paragraph && (
          <CardDescription
            text={
              typeof paragraph === 'function'
                ? paragraph({ customerName: capitalize(customerName || ''), workLifeServiceDependentCareCode })
                : paragraph
            }
          />
        )}
      </>
      <BulletsContainer>
        {bullets.map((bulletPoint, index) => (
          <BulletItem key={'bulletItem_' + index}>
            <View style={{ marginRight: 8 }}>
              <CheckMarkBullet size={25} />
            </View>

            <BulletText key={'bulletText_' + index} text={bulletPoint} />
          </BulletItem>
        ))}
      </BulletsContainer>
      {CTA && PATH && (
        <CardButtonContainer>
          <CardCTAButton
            text={CTA}
            onPress={() => {
              track(WORK_LIFE_SERVICES_EVENTS.SELECT_WORKLIFE, { worklife_service: title, customerName })
              IS_WEB ? openUrl(PATH) : WebBrowser.openBrowserAsync(PATH)
            }}
            testID={`${identifiers?.dataTestId}Button`}
          />
        </CardButtonContainer>
      )}
    </Card>
  )
}
